import { Button, ButtonVariants, DeviceMediaRequest, TargetMediaResponse, getApiUrl, getMediaRequestStatusesDisplayName } from "@app/shared";
import { MediaRequestStatuses } from "@app/shared/src/models/Shared.Models.Api.V1.MediaRequestStatuses.gen";
import copy from "copy-to-clipboard";
import { useCallback } from "react";
import { formatByteSize, toastSuccess } from "../../../helpers";
import { useMediaRequestsHub } from "../../../hooks";
import { IconLink, Icons } from "../../shared";
import { HoverTooltip } from "../../shared/HoverTooltip";
import { MediaNoticeList } from "../../shared/MediaNoticeList";
import { isRadarNotice, visibleNotices } from "../../shared/mediaNoticeHelpers";

type TargetVideoSectionProps = {
    targetMediaResponse: TargetMediaResponse;
    onRequestVideo: () => void;
    deviceId?: string;
    targetId?: string;
    onStatusUpdate?: (response: TargetMediaResponse) => void;
};

export const TargetVideoSection: React.FC<TargetVideoSectionProps> = ({ targetMediaResponse, onRequestVideo, deviceId, onStatusUpdate }) => {
    const { deviceMediaRequest } = targetMediaResponse;

    const handleRequestsUpdate = useCallback((updated: DeviceMediaRequest[]) => {
        if (!deviceMediaRequest || !onStatusUpdate) return;
        const match = updated.find(u => u.id === deviceMediaRequest.id);
        if (match) {
            onStatusUpdate(new TargetMediaResponse({ ...targetMediaResponse, deviceMediaRequest: match } as any));
        }
    }, [deviceMediaRequest, onStatusUpdate, targetMediaResponse]);

    useMediaRequestsHub(deviceId, handleRequestsUpdate, !deviceMediaRequest);

    if (!deviceMediaRequest) {
        /*
         * Two different situations look identical from here — no media request row — and they want
         * opposite affordances.
         *
         * A trigger with video capture on stamps its event and tells the unit to cut a clip immediately;
         * that request then takes minutes to reach us. Offering "Request" during that window asks someone
         * to order footage that is already being captured, and the clip then appears on its own, which
         * reads as though the button did nothing or as though the system is acting on its own.
         *
         * So only offer to pull footage when nothing has asked for it yet.
         */
        if (targetMediaResponse.clipAlreadyRequested)
            return (
                <div className="text-center space-y-2">
                    <p className="text-gray-600">Video is being prepared</p>
                    <p className="text-sm text-gray-500">
                        This event asked the unit for footage automatically. It usually arrives within a few
                        minutes — no action needed. Reopen this target to check again.
                    </p>
                </div>
            );

        return (
            <div className="text-center space-y-4">
                <p className="text-gray-600">Request the video</p>
                <Button variant={ButtonVariants.Primary} onClick={onRequestVideo} className="inline-flex items-center">
                    <Icons.Video className="w-4 h-4 mr-2" />
                    Request
                </Button>
            </div>
        );
    }

    const { status: requestStatus, id: requestId, deviceId: requestDeviceId, videoUrl } = deviceMediaRequest;

    const idFooter = requestId ? (
        <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
            <HoverTooltip hoverable={<span>ID: {requestId.split("-")[0]}…</span>}>{requestId}</HoverTooltip>
            <IconLink
                Icon={Icons.Clipboard}
                onClick={() => {
                    copy(requestId);
                    toastSuccess("Copied", undefined, 1500);
                }}
            />
        </div>
    ) : null;

    const handleVideoView = () => {
        window.open(getApiUrl(`/api/mediarequest/${requestDeviceId}/media-requests/${requestId}/video/view`), "_blank");
    };

    const handleVideoDownload = () => {
        window.open(getApiUrl(`/api/mediarequest/${requestDeviceId}/media-requests/${requestId}/video/download`), "_blank");
    };
    // Show the video as soon as its URL is available, regardless of overall request status.
    // Partial completion (e.g. radar leg still in flight, or radar upload failed) shouldn't
    // hide a usable video.
    if (videoUrl && requestId && requestDeviceId) {
        return (
            <div className="w-full space-y-4">
                <div className="text-center">
                    <p className="text-gray-600 mb-4">Your video is ready!</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button variant={ButtonVariants.Primary} onClick={handleVideoView} className="inline-flex items-center">
                            <Icons.Play className="w-4 h-4 mr-2" />
                            Watch Video
                        </Button>
                        <Button variant={ButtonVariants.Secondary} onClick={handleVideoDownload} className="inline-flex items-center">
                            <Icons.Download className="w-4 h-4 mr-2" />
                            Download
                        </Button>
                    </div>
                </div>
                {idFooter}
            </div>
        );
    }

    if (requestStatus === MediaRequestStatuses.Expired || requestStatus === MediaRequestStatuses.Cancelled || requestStatus === MediaRequestStatuses.Error) {
        const isError = requestStatus === MediaRequestStatuses.Error;
        const buttonText = isError ? "Request Video Again" : "Request Video";

        const statusMessage = isError ? "An error occurred while processing your video request." : `Video request was ${getMediaRequestStatusesDisplayName(requestStatus)?.toLowerCase()}.`;

        // This popup is about the video, so drop radar notices — the viewer didn't come here for radar
        // and a radar-merge failure is just noise (a video request can still surface one). On hover we
        // show the curated, user-safe quality notices rather than the raw device error message.
        const videoNotices = isError ? visibleNotices(deviceMediaRequest.notices, false).filter((n) => !isRadarNotice(n)) : [];

        return (
            <div className="text-center space-y-4">
                {isError ? (
                    <HoverTooltip
                        hoverable={
                            <p className="inline-flex items-center justify-center gap-1.5 text-red-600 cursor-help">
                                <span>{statusMessage}</span>
                                <Icons.Info className="h-4 w-4 shrink-0 text-brand-primary" aria-label="More details" />
                            </p>
                        }
                    >
                        {videoNotices.length ? (
                            <MediaNoticeList notices={deviceMediaRequest.notices} exclude={isRadarNotice} className="max-w-xs text-left" />
                        ) : (
                            <span className="text-sm">The video couldn’t be produced for the requested moment.</span>
                        )}
                    </HoverTooltip>
                ) : (
                    <p className="text-gray-600">{statusMessage}</p>
                )}
                <Button variant={isError ? ButtonVariants.Secondary : ButtonVariants.Primary} onClick={onRequestVideo} className="inline-flex items-center">
                    <Icons.Video className="w-4 h-4 mr-2" />
                    {buttonText}
                </Button>
                {idFooter}
            </div>
        );
    }

    if (requestStatus === MediaRequestStatuses.Recording || requestStatus === MediaRequestStatuses.Merging || requestStatus === MediaRequestStatuses.Uploading || requestStatus === MediaRequestStatuses.Scheduled) {
        const isUploading = requestStatus === MediaRequestStatuses.Uploading;
        const progressPct = Math.round((deviceMediaRequest.progress ?? 0) * 100);
        const rate = deviceMediaRequest.bytesPerSecond ? ` at ${formatByteSize(deviceMediaRequest.bytesPerSecond)}/s` : "";
        return (
            <div className="text-center space-y-4">
                <div className="flex items-center justify-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-lg font-medium text-gray-700">
                        {getMediaRequestStatusesDisplayName(requestStatus)}{isUploading ? `: ${progressPct}%${rate}` : "..."}
                    </span>
                </div>
                <p className="text-gray-600">Please wait while your video is being processed.</p>
                {idFooter}
            </div>
        );
    }
};
